import { Action, Reducer } from "redux";
import { AppThunkAction } from ".";
import { Dictionary, EnumConfigMap, IWithArchivation } from "../entities/common";
import { get, post } from "../fetch-interceptor";
import { IDeletionResult, IEntityStore, StoreHelper } from "./services/storeHelper";
import { catchApiError, defaultCatch } from "./utils";
import {
    Calendar, CalendarStatus, CreateOrUpdateOrDeleteCalendarExceptionsModel, WorkingTimeRange, prepareCalendar
} from "./calendar/common";
import { downloadCsv } from "../components/utils/csvDownload";
import * as Notifications from "./NotificationsStore";
import { push, RouterAction } from "react-router-redux";

export const CalendarStatusMap: EnumConfigMap = {
    [CalendarStatus.Active]: { title: "Active", cssClassName: "Active" },
    [CalendarStatus.Inactive]: { title: "Inactive", cssClassName: "Inactive" },
    [CalendarStatus.Draft]: { title: "Draft", cssClassName: "Draft" }
}

export interface CalendarsState extends IEntityStore<Calendar> {
    isLoading: boolean;
    isListLoading: boolean;
    isLoaded: boolean;
    deletionResult?: IDeletionResult[];
    updating: 'exceptions' | 'workDayExpectedHrs' | undefined;
    defaultCalendarId: string;
    archivedById: Dictionary<Calendar>;
}

export function getEntityCalendar<T extends IWithArchivation>(calendars: CalendarsState, entity: T, calendarId: string): Calendar {
    return entity.isArchived
        ? calendars.archivedById[calendarId]
        : calendars.byId[calendarId];
}

interface RequestCalendarsAction {
    type: 'REQUEST_CALENDARS';
}

interface ReceivedCalendarsAction {
    type: 'RECEIVED_CALENDARS';
    calendars: Calendar[];
}

interface ReceivedArchivedCalendarsAction {
    type: 'RECEIVED_ARCHIVED_CALENDARS';
    calendars: Calendar[];
}

interface LoadCalendarAction {
    type: 'LOAD_CALENDAR';
    id?: string;
}

interface LoadedCalendarAction {
    type: 'LOADED_CALENDAR';
    calendar: Calendar;
}

interface UpdateCalendarSuccessAction {
    type: 'UPDATE_CALENDAR_SUCCESS';
    calendar: Calendar;
}

interface BulkUpdateCalendarsSuccessAction {
    type: 'BULK_UPDATE_CALENDARS_SUCCESS';
    calendars: Calendar[];
}

interface ReceivedDeleteCalendarsResultAction {
    type: 'RECEIVED_REMOVE_CALENDARS_RESULT';
    deletionResult?: IDeletionResult[];
}
interface CalendarUpdatingAction {
    type: 'CALENDAR_UPDATING';
    updating?: 'exceptions' | 'workDayExpectedHrs';
}

export type KnownAction =
    | LoadCalendarAction
    | LoadedCalendarAction
    | UpdateCalendarSuccessAction
    | RequestCalendarsAction
    | ReceivedCalendarsAction
    | ReceivedArchivedCalendarsAction
    | BulkUpdateCalendarsSuccessAction
    | ReceivedDeleteCalendarsResultAction
    | CalendarUpdatingAction;

const unloadedState: CalendarsState = {
    byId: {},
    allIds: [],
    isLoading: false,
    isListLoading: false,
    isLoaded: false,
    updating: undefined,
    defaultCalendarId: undefined as any as string,
    archivedById: {},
};

export const actionCreators = {
    ensureCalendars: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState();
        if (state.calendars.isLoaded) {
            return;
        }
        get<Calendar[]>(`api/calendar`)
            .then(data => dispatch({ type: 'RECEIVED_CALENDARS', calendars: data }))
            .catch(defaultCatch(dispatch));

        dispatch({ type: 'REQUEST_CALENDARS' })
    },
    loadCalendar: (id: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        get<Calendar>(`api/calendar/${id}`)
            .then(data => dispatch({ type: 'LOADED_CALENDAR', calendar: data }))
            .catch(defaultCatch(dispatch));

        dispatch({ type: 'LOAD_CALENDAR', id: id });
    },
    loadArchivedForProject: (projectId: string): AppThunkAction<KnownAction> => (dispatch) => {
        get<Calendar[]>(`api/archivedproject/${projectId}/calendar`)
            .then(calendars => dispatch({ type: 'RECEIVED_ARCHIVED_CALENDARS', calendars }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: 'LOAD_CALENDAR' });
    },
    updateAttributes: (calendarId: string, updates: Dictionary<any>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Calendar>(`api/calendar/${calendarId}/attributes`, updates)
            .then(data => {
                dispatch({ type: 'UPDATE_CALENDAR_SUCCESS', calendar: data });
            })
            .catch(defaultCatch(dispatch));
    },
    updateWorkWeek: (calendarId: string, workDayWorkingTimes?: Dictionary<WorkingTimeRange[]> | null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Calendar>(`api/calendar/${calendarId}/workweek`, {
            updateHours: workDayWorkingTimes !== undefined,
            workDayWorkingTimes: workDayWorkingTimes
        })
            .then(data => {
                dispatch({ type: "LOADED_CALENDAR", calendar: data });
                dispatch({ type: 'CALENDAR_UPDATING', updating: undefined });
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: 'CALENDAR_UPDATING', updating: 'workDayExpectedHrs' });
    },
    updateExceptions: (calendarId: string, update: CreateOrUpdateOrDeleteCalendarExceptionsModel): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<Calendar>(`api/calendar/${calendarId}/exceptions`, update)
            .then(data => {
                dispatch({ type: "LOADED_CALENDAR", calendar: data });
                dispatch({ type: 'CALENDAR_UPDATING', updating: undefined });
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: 'CALENDAR_UPDATING', updating: 'exceptions' });
    },
    createCalendar: (name: string): AppThunkAction<KnownAction | RouterAction> => (dispatch) => {
        post<Calendar>(`api/calendar`, { name })
            .then(data => {
                dispatch({ type: 'BULK_UPDATE_CALENDARS_SUCCESS', calendars: [data] });
                dispatch(push(`/calendar/${data.id}`, {}))
            })
            .catch(defaultCatch(dispatch));
    },
    removeCalendars: (ids: string[], returnToBack?: boolean): AppThunkAction<KnownAction | RouterAction> => (dispatch) => {
        post<IDeletionResult[]>(`api/calendar/bulkDelete`, { ids })
            .then(data =>{
                 dispatch({ type: 'RECEIVED_REMOVE_CALENDARS_RESULT', deletionResult: data });
                if (returnToBack) {
                    dispatch(push('/calendars', {}));
                }
            })
            .catch(defaultCatch(dispatch));
    },
    dismissDeletionResult: (): AppThunkAction<KnownAction> => (dispatch) => {
        dispatch({ type: 'RECEIVED_REMOVE_CALENDARS_RESULT' });
    },
    bulkUpdateCalendars: (updates: Dictionary<Dictionary<CalendarStatus>>): AppThunkAction<KnownAction> => (dispatch) => {
        post<Calendar[]>(`api/calendar/bulkUpdate`, updates)
            .then(data => dispatch({ type: 'BULK_UPDATE_CALENDARS_SUCCESS', calendars: data }))
            .catch(defaultCatch(dispatch));
    },
    makeCalendarDefault: (calendarId: string): AppThunkAction<KnownAction> => (dispatch) => {
        post<Calendar[]>(`api/calendar/${calendarId}/default`, {})
            .then(data => dispatch({ type: 'BULK_UPDATE_CALENDARS_SUCCESS', calendars: data }))
            .catch(defaultCatch(dispatch));
    },
    cloneCalendar: (calendarId: string): AppThunkAction<KnownAction | RouterAction> => (dispatch) => {
        post<Calendar>(`api/calendar/${calendarId}/clone`, {})
            .then(data =>{
                 dispatch({ type: 'BULK_UPDATE_CALENDARS_SUCCESS', calendars: [data] });
                 dispatch(push(`/calendar/${data.id}`, {}));
            })
            .catch(defaultCatch(dispatch));
    },
    exportExceptionsToFile: (calendarId: string, fields: string[], ids: string[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        post<string>(`api/calendar/${calendarId}/exceptions/exportToFile`, { fields, ids })
            .then(data => downloadCsv(data, 'Tenant Calendar Exceptions.csv'))
            .catch(defaultCatch(dispatch));
    },
    importExceptionsFromFile: (calendarId: string, file: File): AppThunkAction<KnownAction | Notifications.KnownAction> => (dispatch, getState) => {
        const formData = new FormData();
        formData.set('data', file);
        post<Calendar>(`api/calendar/${calendarId}/exceptions/importFromFile`, formData)
            .then(data => {
                dispatch({ type: "LOADED_CALENDAR", calendar: data });
                dispatch({ type: 'CALENDAR_UPDATING', updating: undefined });
            })
            .catch(catchApiError(_ => {
                dispatch(Notifications.actionCreators
                    .pushNotification({ message: `Failed to import. ${_ || ""}`, type: Notifications.NotificationType.Error }))
            }));
    },
};

export const reducer: Reducer<CalendarsState> = (state: CalendarsState = unloadedState, incomingAction: Action) => {
    const action = incomingAction as KnownAction;
    switch (action.type) {
        case 'REQUEST_CALENDARS':
            return {
                ...state,
                isListLoading: true
            };
        case 'RECEIVED_CALENDARS':{
            const calendars = prepareCalendars(action.calendars);
            const store = StoreHelper.create(calendars);
            return {
                ...state,
                ...store,
                byId: { ...state.archivedById, ...store.byId },
                isListLoading: false,
                isLoaded: true,
                defaultCalendarId: findDefaultCalendar(calendars)?.id ?? state.defaultCalendarId
            };
        }
        case 'RECEIVED_ARCHIVED_CALENDARS': {
            const archivedById = {...state.archivedById };
            prepareCalendars(action.calendars).forEach(_ => archivedById[_.id] = _);

            return {
                ...state,
                archivedById,
                isLoading: false,
            };
        }
        case 'LOAD_CALENDAR':
            return {
                ...state,
                activeEntityId: action.id,
                activeEntity: state.activeEntity?.id === action.id ? state.activeEntity : undefined,
                isLoading: true
            };
        case 'LOADED_CALENDAR':
            {
                const calendar = prepareCalendar(action.calendar);
                return {
                    ...state,
                    ...StoreHelper.addOrUpdate(state, calendar),
                    activeEntityId: calendar?.id,
                    activeEntity: calendar,
                    isLoading: false,
                    defaultCalendarId: isDefaultCalendar(calendar) ? calendar.id : state.defaultCalendarId
                };
            }            
        case 'BULK_UPDATE_CALENDARS_SUCCESS': {
            const calendars = prepareCalendars(action.calendars);
            let nextState = state;
            for (const calendar of calendars) {
                nextState = StoreHelper.addOrUpdate(nextState, calendar) as CalendarsState;
            }
            const newDefault = calendars.find(isDefaultCalendar);
            return newDefault ? { ...nextState, defaultCalendarId: newDefault.id } : nextState;
        }
        case 'RECEIVED_REMOVE_CALENDARS_RESULT': {
            if (!action.deletionResult) {
                return { ...state, deletionResult: undefined };
            }
            const deletedIds = action.deletionResult.filter(_ => _.isDeleted).map(_ => _.id);
            let nextState: CalendarsState = { ...state, deletionResult: action.deletionResult };
            for (const id of deletedIds) {
                nextState = StoreHelper.remove(nextState, id) as CalendarsState;
            }
            return nextState;
        }
        case 'UPDATE_CALENDAR_SUCCESS': {
            const calendar = prepareCalendar(action.calendar);
            return {
                ...state,
                ...StoreHelper.addOrUpdate(state, calendar),
                isLoading: false,
                defaultCalendarId: isDefaultCalendar(calendar) ? calendar.id : state.defaultCalendarId
            };
        }

        default: return state;
    }
};

const prepareCalendars = (calendars: Calendar[]): Calendar[] => calendars.map(prepareCalendar);
const isDefaultCalendar = (calendar: Calendar) => calendar.isDefault && !calendar.isUserCalendar;
const findDefaultCalendar = (calendars: Calendar[]): Calendar | undefined =>
    calendars.find(isDefaultCalendar) ?? calendars.find(_ => !_.isUserCalendar) ?? calendars[0];